import DocumentMeta from "react-document-meta";
import ExampleLandingHeader from "../components/ExampleLandingHeader";
import { useTranslation } from "../i18n/LanguageContext";
import FloatingWhatsAppButton from "../components/FloatingWhatsAppButton";
import SocialLinksSection from "../components/SocialLinksSection";
import { buildNoIndexMeta } from "../seo";

const copy = {
    it: {
        title: "Casa Vacanza Miraggio",
        metaTitle: "Casa Vacanza Miraggio | Relax vicino al mare",
        metaDescription: "Casa vacanza con spazi ampi, cucina attrezzata e giardino. Prenota direttamente.",
        introBanner: "Spazi ampi, cucina attrezzata e privacy totale per una vacanza in famiglia o con amici.",
        ctaBanner: "Verifica le date",
        menu: [
            { label: "Alloggi", href: "#camere" },
            { label: "Servizi", href: "#servizi" },
            { label: "Contatto", href: "#contatto" }
        ],
        servicesTitle: "Servizi della casa",
        servicesList: [
            "Cucina completa e barbecue in giardino",
            "Wi‑Fi veloce e smart TV",
            "Parcheggio privato e self check‑in"
        ],
        checkInLabel: "Check‑in",
        checkInValue: "Dalle 16:00",
        checkOutLabel: "Check‑out",
        checkOutValue: "Entro le 10:30",
        roomsTitle: "Alloggi e tariffe",
        rooms: [
            { title: "Bilocale", body: "Da €120 a notte" },
            { title: "Trilocale vista giardino", body: "Da €150 a notte" },
            { title: "Villetta con veranda", body: "Da €190 a notte" }
        ],
        calendarTitle: "Calendario disponibilità",
        calendarSubtitle: "Seleziona le date per vedere gli slot disponibili.",
        calendarLegendAvailable: "Disponibile",
        calendarLegendBusy: "Occupato",
        calendarLegendSelected: "Selezionato",
        calendarNote: "Calendario dimostrativo: il form non invia prenotazioni.",
        calendarMonth: "Luglio 2026",
        contactTitle: "Richiedi disponibilità",
        contactSubtitle: "Ti rispondiamo con un preventivo personalizzato.",
        contactCta1: "Scrivici",
        contactCta2: "Chiama ora"
    },
    en: {
        title: "Casa Vacanza Miraggio",
        metaTitle: "Casa Vacanza Miraggio | Relax near the sea",
        metaDescription: "Holiday home with spacious rooms, full kitchen, and garden. Book directly.",
        introBanner: "Spacious rooms, full kitchen and total privacy for families or friends.",
        ctaBanner: "Check dates",
        menu: [
            { label: "Units", href: "#camere" },
            { label: "Services", href: "#servizi" },
            { label: "Contact", href: "#contatto" }
        ],
        servicesTitle: "Home services",
        servicesList: [
            "Full kitchen and garden barbecue",
            "Fast Wi‑Fi and smart TV",
            "Private parking and self check‑in"
        ],
        checkInLabel: "Check‑in",
        checkInValue: "From 4:00 pm",
        checkOutLabel: "Check‑out",
        checkOutValue: "By 10:30 am",
        roomsTitle: "Units and rates",
        rooms: [
            { title: "One‑bedroom apartment", body: "From €120 per night" },
            { title: "Two‑bedroom garden view", body: "From €150 per night" },
            { title: "Villa with veranda", body: "From €190 per night" }
        ],
        calendarTitle: "Availability calendar",
        calendarSubtitle: "Pick dates to see available slots.",
        calendarLegendAvailable: "Available",
        calendarLegendBusy: "Booked",
        calendarLegendSelected: "Selected",
        calendarNote: "Demo calendar: the form does not submit bookings.",
        calendarMonth: "July 2026",
        contactTitle: "Check availability",
        contactSubtitle: "We’ll reply with a tailored quote.",
        contactCta1: "Message us",
        contactCta2: "Call now"
    },
    de: {
        title: "Casa Vacanza Miraggio",
        metaTitle: "Casa Vacanza Miraggio | Entspannung am Meer",
        metaDescription: "Ferienhaus mit viel Platz, Küche und Garten. Direkt buchen.",
        introBanner: "Viel Platz, voll ausgestattete Küche und Privatsphäre für Familien oder Freunde.",
        ctaBanner: "Termine prüfen",
        menu: [
            { label: "Unterkünfte", href: "#camere" },
            { label: "Services", href: "#servizi" },
            { label: "Kontakt", href: "#contatto" }
        ],
        servicesTitle: "Haus‑Services",
        servicesList: [
            "Voll ausgestattete Küche und Grill im Garten",
            "Schnelles WLAN und Smart‑TV",
            "Privater Parkplatz und Self‑Check‑in"
        ],
        checkInLabel: "Check‑in",
        checkInValue: "Ab 16:00",
        checkOutLabel: "Check‑out",
        checkOutValue: "Bis 10:30",
        roomsTitle: "Unterkünfte und Preise",
        rooms: [
            { title: "Zwei‑Zimmer‑Apartment", body: "Ab €120 pro Nacht" },
            { title: "Drei‑Zimmer mit Gartenblick", body: "Ab €150 pro Nacht" },
            { title: "Villa mit Veranda", body: "Ab €190 pro Nacht" }
        ],
        calendarTitle: "Verfügbarkeitskalender",
        calendarSubtitle: "Wähle Daten, um freie Slots zu sehen.",
        calendarLegendAvailable: "Frei",
        calendarLegendBusy: "Belegt",
        calendarLegendSelected: "Ausgewählt",
        calendarNote: "Demo‑Kalender: das Formular sendet keine Buchungen.",
        calendarMonth: "Juli 2026",
        contactTitle: "Verfügbarkeit anfragen",
        contactSubtitle: "Wir senden ein individuelles Angebot.",
        contactCta1: "Nachricht senden",
        contactCta2: "Jetzt anrufen"
    }
};

const AlbergoPage = () => {
    const { language } = useTranslation();
    const t = copy[language] || copy.it;

    const meta = buildNoIndexMeta({
        title: t.metaTitle,
        description: t.metaDescription,
        path: "/estate-2026/casa-vacanza"
    });

    return (
        <>
            <DocumentMeta {...meta} />
            <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-sky-50 text-stone-900">
                <header className="px-6 pt-10 pb-10 md:px-12 lg:px-20">
                    <ExampleLandingHeader title={t.title} items={t.menu} />
                    <div className="mt-8 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
                        <div className="rounded-3xl bg-amber-700 p-8 text-white shadow-lg shadow-amber-200">
                            <p className="text-sm uppercase tracking-[0.4em] text-amber-200">Estate 2026</p>
                            <p className="mt-4 text-lg text-amber-100">{t.introBanner}</p>
                            <a href="#contatto" className="mt-6 inline-flex rounded-full bg-white px-6 py-3 text-sm font-semibold text-amber-800">
                                {t.ctaBanner}
                            </a>
                        </div>
                        <div id="servizi" className="rounded-3xl bg-white p-8 shadow-lg">
                            <h2 className="text-2xl font-semibold">{t.servicesTitle}</h2>
                            <ul className="mt-4 space-y-3 text-amber-700">
                                {t.servicesList.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                            <div className="mt-6 grid gap-4 sm:grid-cols-2">
                                <div className="rounded-2xl bg-amber-50 p-4">
                                    <p className="text-sm font-semibold">{t.checkInLabel}</p>
                                    <p className="mt-2 text-lg">{t.checkInValue}</p>
                                </div>
                                <div className="rounded-2xl bg-amber-50 p-4">
                                    <p className="text-sm font-semibold">{t.checkOutLabel}</p>
                                    <p className="mt-2 text-lg">{t.checkOutValue}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8 overflow-hidden rounded-3xl shadow-lg">
                        <img
                            src="https://images.unsplash.com/photo-1507089947368-19c1da9775ae?q=80&w=1600&auto=format&fit=crop"
                            alt="Casa vacanza con veranda e giardino"
                            className="h-64 w-full object-cover md:h-96"
                            loading="lazy"
                        />
                    </div>
                </header>

                <main className="px-6 pb-24 md:px-12 lg:px-20">
                    <section id="camere" className="rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.roomsTitle}</h2>
                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            {t.rooms.map((item) => (
                                <div key={item.title} className="rounded-2xl border border-amber-100 p-4">
                                    <p className="font-semibold">{item.title}</p>
                                    <p className="mt-2 text-sm text-amber-700">{item.body}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        <img
                            src="https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=1400&auto=format&fit=crop"
                            alt="Soggiorno con cucina attrezzata"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1449844908441-8829872d2607?q=80&w=1400&auto=format&fit=crop"
                            alt="Veranda con tavolo all'aperto"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                    </div>

                    <section className="mt-10 rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <h2 className="text-2xl font-semibold">{t.calendarTitle}</h2>
                                <p className="mt-2 text-sm text-amber-700">{t.calendarSubtitle}</p>
                            </div>
                            <div className="rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700">
                                {t.calendarMonth}
                            </div>
                        </div>
                        <div className="mt-6 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
                            <div className="grid grid-cols-7 gap-2 text-center text-xs text-stone-600">
                                {["L", "M", "M", "G", "V", "S", "D"].map((day) => (
                                    <div key={day} className="rounded-lg bg-amber-50 py-2 font-semibold text-amber-700">
                                        {day}
                                    </div>
                                ))}
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => {
                                    const isBusy = [3, 4, 5, 10, 11, 12, 18, 19, 20, 27, 28].includes(date);
                                    const isSelected = [21, 22, 23].includes(date);
                                    const baseClass = "rounded-lg py-3 font-semibold";
                                    const stateClass = isSelected
                                        ? "bg-amber-600 text-white"
                                        : isBusy
                                          ? "bg-stone-200 text-stone-500"
                                          : "bg-white text-stone-700 border border-amber-100";
                                    return (
                                        <div key={date} className={`${baseClass} ${stateClass}`}>
                                            {date}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="rounded-3xl bg-amber-50 p-6">
                                <h3 className="text-lg font-semibold text-amber-800">Richiesta di prenotazione</h3>
                                <div className="mt-4 grid gap-3 text-sm">
                                    <div className="grid gap-2 sm:grid-cols-2">
                                        <div className="rounded-xl border border-amber-200 bg-white px-4 py-3 text-amber-700">Check‑in</div>
                                        <div className="rounded-xl border border-amber-200 bg-white px-4 py-3 text-amber-700">Check‑out</div>
                                    </div>
                                    <div className="rounded-xl border border-amber-200 bg-white px-4 py-3 text-amber-700">Numero ospiti</div>
                                    <button type="button" className="rounded-full bg-amber-700 px-6 py-3 text-sm font-semibold text-white">
                                        Richiedi disponibilità
                                    </button>
                                </div>
                                <p className="mt-4 text-xs text-amber-700">{t.calendarNote}</p>
                                <div className="mt-4 flex flex-wrap gap-3 text-xs text-amber-700">
                                    <span className="flex items-center gap-2">
                                        <span className="h-3 w-3 rounded bg-white border border-amber-200"></span>
                                        {t.calendarLegendAvailable}
                                    </span>
                                    <span className="flex items-center gap-2">
                                        <span className="h-3 w-3 rounded bg-stone-200"></span>
                                        {t.calendarLegendBusy}
                                    </span>
                                    <span className="flex items-center gap-2">
                                        <span className="h-3 w-3 rounded bg-amber-600"></span>
                                        {t.calendarLegendSelected}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <SocialLinksSection className="mt-10" />

                    <section id="contatto" className="mt-10 rounded-3xl bg-amber-800 p-6 text-white shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.contactTitle}</h2>
                        <p className="mt-2 text-amber-100">{t.contactSubtitle}</p>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                            <a href="/estate-2026#contatto" className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-amber-800">
                                {t.contactCta1}
                            </a>
                            <a href="tel:+393515699197" className="rounded-full border border-white px-6 py-3 text-sm font-semibold text-white">
                                {t.contactCta2}
                            </a>
                        </div>
                    </section>
                </main>
                <FloatingWhatsAppButton />
            </div>
        </>
    );
};

export default AlbergoPage;
